import { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Skeleton, Tab, Tabs } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useAppContext } from 'hooks/useAppContext';
import {
  PrismaticAuthResponse,
  authenticatePrismaticAPI
} from 'services/prismaticService';
import './Integrations.scss';
import { IntegrationsErrorBoundary } from './IntegrationsErrorBoundary';
import { IntegrationsLogs } from './IntegrationsLogs';
import { IntegrationsMarketplace } from './IntegrationsMarketplace';
import { IntegrationsWorkflows } from './IntegrationsWorkflows';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`integrations-tabpanel-${index}`}
    aria-labelledby={`integrations-tab-${index}`}
    {...other}
  >
    {value === index && <Box>{children}</Box>}
  </div>
);

interface TabConfig {
  label: string;
  component: React.ComponentType;
  feature?: string;
}

const ALL_TABS: TabConfig[] = [
  {
    label: 'Marketplace',
    component: IntegrationsMarketplace,
    feature: 'marketplace'
  },
  {
    label: 'Custom Workflows',
    component: IntegrationsWorkflows,
    feature: 'workflow_builder'
  },
  { label: 'Logs', component: IntegrationsLogs, feature: 'logs' }
];

export const Integrations = () => {
  const { organizationInfo, selectedOrganization } = useAppContext();
  const [activeTab, setActiveTab] = useState(0);
  const [authData, setAuthData] = useState<PrismaticAuthResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch Prismatic authentication data
  useEffect(() => {
    const fetchAuth = async () => {
      if (!selectedOrganization?.id) return;

      try {
        setIsLoading(true);
        setError(null);
        const data = await authenticatePrismaticAPI(selectedOrganization.id);
        setAuthData(data);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Authentication failed')
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchAuth();
  }, [selectedOrganization?.id]);

  // Calculate visible tabs based on enabled features
  const visibleTabs = useMemo(() => {
    // Default to marketplace only if no auth data or error
    if (!authData || error) {
      return [ALL_TABS[0]];
    }

    return ALL_TABS.filter((tab) => {
      // Always show tabs without feature requirements
      if (!tab.feature) return true;

      // Check if the feature is enabled in Prismatic
      return authData.enabledFeatures.includes(tab.feature);
    });
  }, [authData, error]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (!organizationInfo?.features || isLoading) {
    return (
      <div className="integrations-container">
        <Paper sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 1 }}>
            <Skeleton variant="rectangular" animation="wave" height={48} />
          </Box>
          <Box sx={{ p: 3 }}>
            <Skeleton variant="rectangular" animation="wave" height={600} />
          </Box>
        </Paper>
      </div>
    );
  }

  // Check if integrations feature is enabled
  if (!organizationInfo.features?.integrations2025) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="integrations-container">
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="integrations tabs"
          >
            {visibleTabs.map((tab) => (
              <Tab key={tab.label} label={tab.label} />
            ))}
          </Tabs>
        </Box>

        {visibleTabs.map((tab, index) => (
          <TabPanel key={tab.label} value={activeTab} index={index}>
            <IntegrationsErrorBoundary>
              <tab.component />
            </IntegrationsErrorBoundary>
          </TabPanel>
        ))}
      </Paper>
    </div>
  );
};
